// @flow

import React, { useState, useCallback } from "react";
import type { Node } from "react";
import ReactRouterPropTypes from "react-router-prop-types";

import {
    dateFromDayShift,
    shiftNumber,
    fromShiftTag,
    niceDate,
    getDateOfISOWeekShift,
    classNames,
} from "../../lib/Util";

import type { ShiftNumberData } from "../../lib/Models";

import { updateTsForInsights } from "../../lib/BusinessLogic";

import { INSIGHT_TYPES } from "../../lib/ManufacturingTags.generated";
import { INSIGHT_TYPES_CSS, INSIGHT_ACTIONS_ARRAY, INSIGHT_ACTIONS } from "../../lib/Models";
import { filterInsightsByStateCriteria, getDateClass, highlightCurrShift } from "./InsightsCommon";
import * as t from "../../lib/backend/manufacturing2.generated.types";
import InsightActionsModal from "./InsightsActionsModal";
import ErrorComponent from "../ErrorComponent";
import { translate, getLang } from "../IntlProviderWrapper";
import PlanIcon from "../react/Icon/Plan";
import MeasureIcon from "../react/Icon/Measure";
import DowntimeIcon from "../react/Icon/Downtime";
import PeopleIcon from "../react/Icon/People";
import CalendarIcon from "../react/Icon/Calendar";

import "./Insights.scss";

type IUuidSelected = {
    [key: string]: boolean,
};

type Props = {
    insights: t.IEventDataEx[],
    selected_line_uuids: IUuidSelected,
    selected_insight_types: IUuidSelected,
    show_only_future: boolean,
    history: ReactRouterPropTypes.history,
    showActions: boolean,
    show_links: boolean,
    reportid: string,
    reverse_order?: boolean,
    is_export?: boolean,
    num_days?: number,
    allowed_actions?: $Keys<typeof INSIGHT_ACTIONS>[],
    onActionSelect?: (action: t.IInsightAction, insight: t.IEventDataEx) => void,
};

const newWorkingDay = (shift_obj: ShiftNumberData, ts: number, prev_ts: number, i: number) => {
    const prev_shift_obj = shiftNumber(new Date(prev_ts));
    const prev_shift_date = getDateOfISOWeekShift(prev_shift_obj.shift, prev_shift_obj.week, prev_shift_obj.year);
    const shift_date = getDateOfISOWeekShift(shift_obj.shift, shift_obj.week, shift_obj.year);
    const dateClass = getDateClass(ts);

    if (i === 0 || prev_shift_date.getTime() !== shift_date.getTime()) {
        const shift_start_date = fromShiftTag(shift_obj.shift_tag);
        const shift_start_obj = shiftNumber(shift_start_date);
        const show_current_icon = dateClass === "current-date";

        return [
            <dl key={i + "_added"} className={`dayTitle ${dateClass} insight__table-row`}>
                <div className="insight__table-cell">
                    <span>
                        <strong>
                            {translate("common.week", "Week")}: {shift_obj.week}
                        </strong>
                        , {niceDate(shift_start_date)},{" "}
                        {translate("common.weekday" + shift_start_obj.day_number, "Day")}
                    </span>
                    {show_current_icon && (
                        <i className="insight__calendar-icon">
                            <CalendarIcon size="sm" />
                        </i>
                    )}
                </div>
            </dl>,
        ];
    } else {
        return null;
    }
};

const translatedInsightContent = (insight: t.IEventDataEx) => {
    const user_lang = getLang();
    let title = insight.title;
    let description = insight.description;
    if (insight.extra_data.title_lang && insight.extra_data.title_lang[user_lang]) {
        title = insight.extra_data.title_lang[user_lang];
        description = insight.extra_data.description_lang[user_lang];
    }

    let comment = "";
    if (typeof insight.extra_data.user_comment === "string") {
        comment = insight.extra_data.user_comment.trim();
        if (comment.length > 0) {
            comment = (
                <React.Fragment>
                    {" "}
                    - <span className="text-muted">{insight.extra_data.user_comment}</span>
                </React.Fragment>
            );
        }
    }

    const icon_props = {
        width: 12,
        height: 12,
    };

    let insight_icon;
    switch (INSIGHT_TYPES_CSS[insight.type]) {
        case "simple":
            insight_icon = <MeasureIcon {...icon_props} />;
            break;
        case "insight-downtime":
        case "work":
            insight_icon = <DowntimeIcon {...icon_props} />;
            break;
        case "people":
            insight_icon = <PeopleIcon {...icon_props} />;
            break;
        default:
            insight_icon = <PlanIcon {...icon_props} />;
    }

    return (
        <div className="insight-description">
            <div className="insight__icon-wrapper">{insight_icon}</div>
            <span>
                <b> {title} </b> - {description} {comment}
            </span>
        </div>
    );
};

const Insights = (props: Props) => {
    let {
        insights,
        selected_line_uuids,
        selected_insight_types,
        show_only_future,
        history,
        show_links,
        reportid,
        is_export,
        num_days,
        allowed_actions,
        onActionSelect,
    } = props;

    const [only_future_insights: boolean, setOnlyFutureInsights: func] = useState(true);

    const handleActionsWrapperClick = useCallback((event: SyntheticMouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    }, []);

    if (insights.length === 0) {
        return (
            <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                    <ErrorComponent
                        msg={translate("common.no_insights_available", "No insights available")}
                        type="warning"
                    />
                </td>
            </tr>
        );
    }

    let prev_ts = 0;

    insights = insights.filter(insight =>
        filterInsightsByStateCriteria(insight, selected_line_uuids, selected_insight_types)
    );
    // detect situation when insight's ts belongs to different shift than its content
    updateTsForInsights(insights);

    if (show_only_future && only_future_insights) {
        let firstShiftStart = dateFromDayShift(new Date(), 0).getTime();
        insights = insights.filter(insight => insight.ts >= firstShiftStart);
    }

    if (num_days && num_days > 0) {
        const max_date = new Date();
        max_date.setDate(max_date.getDate() + num_days);
        max_date.setHours(0, 0, 0, 0);
        const max_ts = max_date.getTime();
        insights = insights.filter(insight => insight.ts < max_ts);
    }

    // ok, now sort by "corrected" ts
    insights = insights.sort((a, b) => (props.reverse_order === true ? b.ts - a.ts : a.ts - b.ts));
    let rows = insights.map<Node>((insight: t.IEventDataEx, i: number) => {
        if (!insight.title) {
            return null;
        }
        const shift_obj = shiftNumber(new Date(insight.ts));
        const shift_str = `${shift_obj.shift_day + 1}`;
        const added = newWorkingDay(shift_obj, insight.ts, prev_ts, i);

        const rowClass = [getDateClass(insight.ts), highlightCurrShift(shift_obj)].join(" ").trim();
        prev_ts = insight.ts;
        let line_title = insight.extra_data.line_title || insight.tags.line_title || insight.tags.line_titles || "";
        let line_uuid = insight.tags.line_uuid || "";
        let url = "/manufacturing/lines/microplan/" + line_uuid;
        // Insight url exceptions
        if (insight.type === INSIGHT_TYPES.no_person || insight.type === INSIGHT_TYPES.no_shift) {
            url = "/manufacturing/people/shifts";
        }

        let shouldShowActions = props.showActions !== false && INSIGHT_ACTIONS_ARRAY.includes(insight.type);

        return [
            added,
            <dl className={`${rowClass} insight__table-row`} key={i}>
                <div className="insight__table-cell">
                    <dt>{translate("common.shift", "Shift")}: </dt>
                    <dd
                        onClick={() => {
                            if (show_links) {
                                history.push(url);
                            }
                        }}
                    >
                        <strong>
                            {shift_str} {translate("common.shift", "Shift")}
                        </strong>
                    </dd>
                </div>
                <div className="insight__table-cell">
                    <dt>{translate("common.line", "Line")}: </dt>
                    <dd
                        onClick={() => {
                            if (show_links) {
                                history.push(url);
                            }
                        }}
                    >
                        {line_title}
                    </dd>
                </div>
                <div
                    className="insight__table-cell"
                    title={new Date(insight.ts).toString() + "-" + new Date(insight.ts_to).toString()}
                >
                    <dt>{translate("common.insight", "Insight")}: </dt>
                    <dd
                        className={classNames("insight__description", INSIGHT_TYPES_CSS[insight.type])}
                        onClick={() => {
                            if (props.show_links) {
                                history.push(url);
                            }
                        }}
                    >
                        {translatedInsightContent(insight)}
                        {!is_export && shouldShowActions && (
                            <div className="insight__actions" onClick={handleActionsWrapperClick}>
                                <InsightActionsModal
                                    insight={insight}
                                    shift_str={shift_str}
                                    line_title={line_title}
                                    reportid={reportid}
                                    history={history}
                                    allowed_actions={allowed_actions}
                                    onActionSelect={onActionSelect}
                                />
                            </div>
                        )}
                    </dd>
                </div>
            </dl>,
        ];
    });

    // show more/less row at the beginning of the table
    if (show_only_future) {
        if (only_future_insights) {
            rows.unshift([
                <tr className="toggle-historical" key={rows.length}>
                    <td className="text-center pl-0 pr-0" colSpan={4}>
                        <button
                            className="btn btn-lg btn-outline-primary w-100"
                            onClick={() => setOnlyFutureInsights(false)}
                        >
                            {translate("insights.show_historical", "Show historical insights...")}
                        </button>
                    </td>
                </tr>,
            ]);
        } else {
            rows.unshift([
                <tr className="toggle-historical" key={rows.length}>
                    <td className="text-center pl-0 pr-0" colSpan={4}>
                        <button
                            className="btn btn-lg btn-outline-primary w-100"
                            onClick={() => setOnlyFutureInsights(true)}
                        >
                            {translate("insights.hide_historical", "Hide historical insights...")}
                        </button>
                    </td>
                </tr>,
            ]);
        }
    }

    return rows;
};

Insights.defaultProps = {
    showActions: true,
};

export default Insights;
